import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LandingPage from "@/components/landing/LandingPage";
import LoginPage from "@/components/auth/LoginPage";
import RegisterPage from "@/components/auth/RegisterPage";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import DashboardPage from "@/components/dashboard/DashboardPage";
import PropertiesPage from "@/components/properties/PropertiesPage";
import PropertyDetail from "@/components/properties/PropertyDetail";
import BookingsPage from "@/components/bookings/BookingsPage";
import CleaningPage from "@/components/cleaning/CleaningPage";
import MaintenancePage from "@/components/maintenance/MaintenancePage";
import GuestChatWidget from "@/components/chat/GuestChatWidget";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminDashboard from "@/components/admin/AdminDashboard";
import AdminUsersPage from "@/components/admin/AdminUsersPage";
import AdminSubscriptionsPage from "@/components/admin/AdminSubscriptionsPage";
import AdminBlogPage from "@/components/admin/AdminBlogPage";
import BillingPage from "@/components/billing/BillingPage";
import CleaningVerifyPage from "@/components/cleaning/CleaningVerifyPage";
import CalendarPage from "@/components/calendar/CalendarPage";
import ReviewsPage from "@/components/reviews/ReviewsPage";
import MessagingPage from "@/components/messaging/MessagingPage";
import SmsConsentPage from "@/components/legal/SmsConsentPage";
import BlogListPage from "@/components/blog/BlogListPage";
import BlogPostPage from "@/components/blog/BlogPostPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500">Loading...</div>;
  if (user && user.role === 'admin') return <Navigate to="/admin" replace />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/chat/:propertyId" element={<GuestChatWidget />} />
      <Route path="/verify-cleaning/:taskId" element={<CleaningVerifyPage />} />
      <Route path="/sms-consent" element={<SmsConsentPage />} />
      <Route path="/opt-in" element={<SmsConsentPage />} />
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />

      {/* Admin Routes */}
      <Route element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/subscriptions" element={<AdminSubscriptionsPage />} />
        <Route path="/admin/blog" element={<AdminBlogPage />} />
      </Route>

      {/* Owner Routes */}
      <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/properties" element={<PropertiesPage />} />
        <Route path="/properties/:id" element={<PropertyDetail />} />
        <Route path="/bookings" element={<BookingsPage />} />
        <Route path="/cleaning" element={<CleaningPage />} />
        <Route path="/maintenance" element={<MaintenancePage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/reviews" element={<ReviewsPage />} />
        <Route path="/messaging" element={<MessagingPage />} />
        <Route path="/billing" element={<BillingPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
